import { useQuery } from '@tanstack/react-query';

import { FeatureFlags, getFeatureFlags } from '../../../http/Config/configApi';
import queryKeys from './queryKeys';

const SAFE_DEFAULTS: FeatureFlags = {
  branchOnboarding: false
};

/**
 * Returns evaluated feature-flag states from the Company API backend.
 *
 * - staleTime: 5 minutes — flags change rarely; avoids a round-trip on every mount.
 * - Safe-OFF default while loading or on error: branch surfaces stay hidden until
 *   the backend explicitly confirms the flag is ON. This prevents flash-of-branch-UI
 *   on slow connections and treats any fetch failure as "flag OFF".
 */
const useFeatureFlags = () => {
  const query = useQuery<FeatureFlags>({
    queryKey: [queryKeys.featureFlags],
    queryFn: getFeatureFlags,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Return safe defaults on error so consumers don't need null-checks
    placeholderData: SAFE_DEFAULTS
  });

  // Always return a complete flags object: fall back to safe defaults while
  // loading or when the fetch failed so callers can destructure unconditionally.
  const flags: FeatureFlags = query.data ?? SAFE_DEFAULTS;

  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
    status: query.status,
    ...flags
  };
};

export default useFeatureFlags;
